type QueuedError = {
  error: Error;
  customData?: unknown;
  tags?: string[];
};

const MAX_QUEUED_ERRORS = 50;

let errorQueue: QueuedError[] = [];
let capturing = false;
let previousOnError: OnErrorEventHandler = null;

const toError = (err: string | Error) => (err instanceof Error ? err : new Error(err));

export const hasFunctionalConsent = () => {
  try {
    return window.sprdConsent?.getStatus()?.functional === true;
  } catch {
    return false;
  }
};

const enqueueError = (error: Error, customData?: unknown, tags?: string[]) => {
  if (errorQueue.length >= MAX_QUEUED_ERRORS) {
    return;
  }
  errorQueue.push({ error, customData, tags });
};

const onUnhandledRejection = (event: PromiseRejectionEvent) => {
  const reason = event.reason;
  enqueueError(reason instanceof Error ? reason : new Error(String(reason)), undefined, [
    'UnhandledPromiseRejection',
  ]);
};

/**
 * Capture errors in memory before functional consent / Raygun CDN load.
 * Nothing is sent to Raygun until {@link flushErrorQueue} runs after consent.
 */
export const startPreConsentErrorCapture = () => {
  if (typeof window === 'undefined' || capturing) {
    return;
  }

  capturing = true;
  previousOnError = window.onerror;
  window.onerror = (message, source, lineno, colno, error) => {
    previousOnError?.(message, source, lineno, colno, error);
    enqueueError(error ?? new Error(String(message)));
    return false;
  };
  window.addEventListener('unhandledrejection', onUnhandledRejection);
};

export const stopPreConsentErrorCapture = () => {
  if (typeof window === 'undefined' || !capturing) {
    return;
  }

  capturing = false;
  window.onerror = previousOnError;
  previousOnError = null;
  window.removeEventListener('unhandledrejection', onUnhandledRejection);
};

export const flushErrorQueue = () => {
  if (typeof window === 'undefined' || !window.rg4js) {
    return;
  }

  const queued = errorQueue;
  errorQueue = [];
  for (const item of queued) {
    window.rg4js('send', {
      error: item.error,
      customData: item.customData,
      tags: item.tags,
    });
  }
};

export const loadRaygunScript = (apiKey: string, version: string) => {
  if (typeof document === 'undefined' || document.getElementById('raygun')) {
    return;
  }

  const script = document.createElement('script');
  script.id = 'raygun';
  // Stub + CDN only — no Raygun bootstrap onerror queue. Pre-consent errors are
  // held in our local queue and flushed after functional consent.
  script.text = `
    !function(a,b,c,d,e,f,g){a.RaygunObject=e,a[e]=a[e]||function(){
    (a[e].o=a[e].o||[]).push(arguments)},f=b.createElement(c),g=b.getElementsByTagName(c)[0],
    f.async=1,f.src=d,g.parentNode.insertBefore(f,g)}(window,document,"script","//cdn.raygun.io/raygun4js/raygun.min.js","rg4js");
    rg4js('apiKey', ${JSON.stringify(apiKey)});
    rg4js('setVersion', ${JSON.stringify(version)});
  `;
  document.head.appendChild(script);
};

export const trackError = (err: string | Error, customData?: unknown, tags?: string[]) => {
  if (!err || typeof window === 'undefined') {
    return;
  }

  const error = toError(err);

  if (hasFunctionalConsent() && window.rg4js) {
    window.rg4js('send', { error, customData, tags });
    return;
  }

  // Queue until functional consent — bots never consent, so these are never sent.
  enqueueError(error, customData, tags);
};
